import moveFeaturePreferences from './move-feature-preferences';
import moveThirdPartyFeaturePreferences from './move-third-party-feature-preferences';
import moveIndividualPreference from './move-individual-preference';
import moveInterfaceEnableItems from './move-interface-enable-items';
import convertEditPostPanels from './convert-edit-post-panels';
import isPreferencesData from '../../is-preferences-data';
import { readStoredJSON } from '../../local-storage';
import type { ScopedPreferences } from '../../types';
import type { LegacyData } from './types';

/**
 * Gets the legacy local storage data for a given user.
 *
 * @param userId The user id.
 *
 * @return The local storage data.
 */
function getLegacyData( userId: string | number ): LegacyData | null {
	return readStoredJSON( `WP_DATA_USER_${ userId }` ) as LegacyData | null;
}

/**
 * Converts data from the old `@wordpress/data` package format.
 *
 * @param data The legacy data in its original format.
 *
 * @return The converted data or `undefined` if there was
 *         nothing to convert.
 */
export function convertLegacyData(
	data: LegacyData | null | undefined
): ScopedPreferences | undefined {
	if ( ! data ) {
		return;
	}

	// Move boolean feature preferences from each editor into the
	// preferences store data structure.
	data = moveFeaturePreferences( data, 'core/edit-widgets' );
	data = moveFeaturePreferences( data, 'core/customize-widgets' );
	data = moveFeaturePreferences( data, 'core/edit-post' );
	data = moveFeaturePreferences( data, 'core/edit-site' );

	// Move third party boolean feature preferences from the interface package
	// to the preferences store data structure.
	data = moveThirdPartyFeaturePreferences( data );

	// Move and convert the interface store's `enableItems` data into the
	// preferences data structure.
	data = moveInterfaceEnableItems( data );

	// Move individual ad-hoc preferences from various packages into the
	// preferences store data structure.
	data = moveIndividualPreference(
		data,
		{ from: 'core/edit-post', to: 'core/edit-post' },
		'hiddenBlockTypes'
	);
	data = moveIndividualPreference(
		data,
		{ from: 'core/edit-post', to: 'core/edit-post' },
		'editorMode'
	);
	data = moveIndividualPreference(
		data,
		{ from: 'core/edit-post', to: 'core/edit-post' },
		'panels',
		convertEditPostPanels
	);
	data = moveIndividualPreference(
		data,
		{ from: 'core/editor', to: 'core' },
		'isPublishSidebarEnabled'
	);
	data = moveIndividualPreference(
		data,
		{ from: 'core/edit-post', to: 'core' },
		'isPublishSidebarEnabled'
	);
	data = moveIndividualPreference(
		data,
		{ from: 'core/edit-site', to: 'core/edit-site' },
		'editorMode'
	);

	// The new system is only concerned with persisting
	// 'core/preferences' preferences reducer, so only return that.
	const preferences = data?.[ 'core/preferences' ]?.preferences;
	return isPreferencesData( preferences ) ? preferences : undefined;
}

/**
 * Gets the legacy local storage data for the given user and returns the
 * data converted to the new format.
 *
 * @param userId The user id.
 *
 * @return The converted data or undefined if no local
 *         storage data could be found.
 */
export default function convertLegacyLocalStorageData(
	userId: string | number
): ScopedPreferences | undefined {
	const data = getLegacyData( userId );
	return convertLegacyData( data );
}
